import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Box
} from '@mui/material';
import { QRCodeSVG } from 'qrcode.react';
import { useAuth } from '../../context/AuthContext';
import { authService } from '../../services/authService';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, setupTfa, validateTfa, isAuthenticated } = useAuth();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [tfaCode, setTfaCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [setupTfaMode, setSetupTfaMode] = useState(false);
  const [requestTfaMode, setRequestTfaMode] = useState(false);
  const [tfaQrCode, setTfaQrCode] = useState('');

  const returnUrl = (location.state as any)?.from?.pathname || '/';

  useEffect(() => {
    if (isAuthenticated) {
      navigate(returnUrl);
    }

    const currentUser = authService.getCurrentUser();
    if (currentUser) {
      if (currentUser.setupTfa) {
        setSetupTfaMode(true);
        setTfaQrCode(currentUser.tfaQrCode || '');
      } else if (currentUser.requestTfaCode) {
        setRequestTfaMode(true);
      }
    }
  }, [isAuthenticated, navigate, returnUrl]);

  const getFirstActiveRoute = (): string => {
    const routes = [
      { path: '/coupons', roles: ['coupon:view', 'coupon:tester', 'coupon:edit', 'coupon:admin'] },
      { path: '/notifications', roles: ['notification:view', 'notification:tester', 'notification:edit', 'notification:admin'] },
      { path: '/payments', roles: ['admin'] },
      { path: '/users', roles: ['admin'] },
      { path: '/redemptions', roles: ['reports', 'redeemer'] },
      { path: '/devices', roles: ['token:view'] },
      { path: '/visits', roles: ['visit:view'] },
      { path: '/recordVisits', roles: ['visit:view'] }
    ];

    for (const route of routes) {
      if (authService.hasRole(route.roles)) {
        return route.path;
      }
    }

    return '/';
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(username, password);

      if (result.setupTfa) {
        setSetupTfaMode(true);
        setTfaQrCode(result.tfaQrCode || '');
      } else if (result.requestTfaCode) {
        setRequestTfaMode(true);
      } else {
        const redirectUrl = returnUrl === '/' ? getFirstActiveRoute() : returnUrl;
        navigate(redirectUrl);
      }
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || err?.message || err?.toString() || 'An error occurred. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSetupTfa = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await setupTfa(tfaCode);
      const redirectUrl = returnUrl === '/' ? getFirstActiveRoute() : returnUrl;
      navigate(redirectUrl);
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || err?.message || err?.toString() || 'Invalid TFA code. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleValidateTfa = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await validateTfa(tfaCode);
      const redirectUrl = returnUrl === '/' ? getFirstActiveRoute() : returnUrl;
      navigate(redirectUrl);
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || err?.message || err?.toString() || 'Invalid TFA code. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (setupTfaMode) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
        sx={{
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Card sx={{ maxWidth: 500, width: '100%', m: 2, position: 'relative', zIndex: 2 }}>
          <CardContent>
            <Typography variant="h5" component="h1" gutterBottom>
              Setup Second Factor
            </Typography>
            <Typography variant="body2" paragraph>
              Your account is missing a second time based authenticator configured. Make sure to:<br />
              1. Install Authenticator authenticator (ie Google Authenticator)<br />
              2. Scan the QR code below<br />
              3. Enter the generated 6 digit number<br />
              4. Press "Confirm Code" button
            </Typography>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {!loading ? (
              <form onSubmit={handleSetupTfa}>
                <Box display="flex" justifyContent="center" mb={2}>
                  <QRCodeSVG value={tfaQrCode} size={256} />
                </Box>
                <TextField
                  fullWidth
                  label="TFA Code"
                  value={tfaCode}
                  onChange={(e) => setTfaCode(e.target.value)}
                  required
                  inputProps={{ maxLength: 6 }}
                  autoFocus
                  margin="normal"
                />
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                  sx={{ mt: 2 }}
                >
                  Confirm Code
                </Button>
              </form>
            ) : (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (requestTfaMode) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
        sx={{
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Card sx={{ maxWidth: 400, width: '100%', m: 2, position: 'relative', zIndex: 2 }}>
          <CardContent>
            <Typography variant="h5" component="h1" gutterBottom>
              Validate TFA Code
            </Typography>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {!loading ? (
              <form onSubmit={handleValidateTfa}>
                <TextField
                  fullWidth
                  label="TFA Code"
                  value={tfaCode}
                  onChange={(e) => setTfaCode(e.target.value)}
                  required
                  inputProps={{ maxLength: 6 }}
                  autoFocus
                  margin="normal"
                />
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                  sx={{ mt: 2 }}
                >
                  Validate
                </Button>
              </form>
            ) : (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box 
      display="flex" 
      justifyContent="center" 
      alignItems="center" 
      minHeight="100vh"
      sx={{
        bgcolor: 'background.default',
        px: 2,
        position: 'relative',
        zIndex: 1,
      }}
    >
      <Card 
        sx={{ 
          maxWidth: 420, 
          width: '100%', 
          borderRadius: 2,
          boxShadow: '0px 2px 8px rgba(0,0,0,0.08)',
          position: 'relative',
          zIndex: 2,
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
            <Box
              component="img"
              src="/favicon.svg"
              alt="Coupon Manager Logo"
              sx={{
                width: { xs: 64, sm: 80 },
                height: { xs: 64, sm: 80 },
                mb: 2,
              }}
            />
            <Typography 
              variant="h5" 
              component="h1" 
              sx={{ 
                fontWeight: 500,
                color: 'text.secondary',
                fontSize: { xs: '1.25rem', sm: '1.5rem' },
              }}
            >
              Log In
            </Typography>
          </Box>
          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 3,
                borderRadius: 2,
              }}
            >
              {error}
            </Alert>
          )}
          {!loading ? (
            <form onSubmit={handleLogin}>
              <TextField
                fullWidth
                label="Username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                autoFocus
                margin="normal"
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                margin="normal"
                sx={{ mb: 3 }}
              />
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                size="large"
                sx={{ 
                  mt: 2,
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 600,
                }}
              >
                Login
              </Button>
            </form>
          ) : (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress size={40} />
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default Login;
